import React, { useEffect, useState } from 'react';
import { useClerk, useMagicLink } from '@clerk/clerk-react';
import { EmailLinkFactor } from '@clerk/types/dist/factors';
import { useRecoilValue } from 'recoil';
import { environmentContextState } from '../recoil/atoms';
import { navigate } from '../util';
import { SignInResource } from '@clerk/types/dist/signIn';
import { SignUpResource } from '@clerk/types/dist/signUp';
import { VerificationResource } from '@clerk/types/dist/verification';
import { ClerkResource } from '../models/ClerkResource.model';
import styles from './LoyaltyForm.module.scss';
import { to } from 'await-to-js';
import { Spinner } from '../root/components/Spinner';
import useSafariBfCacheInvalidator from '../hooks/useSafariBfCacheInvalidator';
import { cleanClerkError } from './clerkHelper';

interface Props {
  phoneNumber: string | undefined;
  emailAddress: string;
  startOver: (err?: string) => void;
  clerkResource: ClerkResource;
}

const StatusContainer: React.FC<{ title: string }> = ({ title, children }) => (
  <div className={styles.statusContainer}>
    <h2>{title}</h2>
    {children}
  </div>
);

export const MagicLinkValidation: React.FC<Props> = ({ phoneNumber, emailAddress, startOver, clerkResource }) => {
  const [expired, setExpired] = useState(false);
  const [verified, setVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  useSafariBfCacheInvalidator(setIsLoading);
  const environmentContext = useRecoilValue(environmentContextState);

  const { setSession } = useClerk();
  const resource = clerkResource.type === 'signup' ? clerkResource.signUp : clerkResource.signIn;

  const { startMagicLinkFlow: startMagicLinkFlowSignIn } = useMagicLink(resource as SignInResource); // as SignInResource is a hack to appease TypeScript
  const { startMagicLinkFlow: startMagicLinkFlowSignUp } = useMagicLink(resource as SignUpResource); // as SignUpResource is a hack to appease TypeScript

  const prepareMagicLink = async () => {
    let magicLinkRes: SignInResource | SignUpResource | undefined;
    let magicLinkErr: Error | null;
    let verification: VerificationResource;

    if (clerkResource.type === 'signin') {
      const [err, si] = await to(clerkResource.signIn.create({ identifier: emailAddress }));
      if (err || !si) {
        startOver(cleanClerkError(err, 'Invalid Email'));
        return;
      }
      const emailLinkFirstFactor: EmailLinkFactor = si.supportedFirstFactors.find(
        (ff) => ff.strategy === 'email_link' && ff.safeIdentifier?.toLowerCase() === emailAddress?.toLowerCase(),
      ) as EmailLinkFactor;
      const { emailAddressId } = emailLinkFirstFactor;

      setIsLoading(false);
      [magicLinkErr, magicLinkRes] = await to(
        startMagicLinkFlowSignIn({
          emailAddressId,
          redirectUrl: `https://${environmentContext.domain}/pages/verification`,
        }),
      );

      verification = (magicLinkRes as SignInResource).firstFactorVerification;
    } else {
      const [signupErr, signupResponse] = await to(
        clerkResource.signUp.create({
          emailAddress,
          phoneNumber,
          firstName: clerkResource.firstName,
          lastName: clerkResource.lastName,
        }),
      );

      if (signupErr || !signupResponse) {
        startOver((signupErr as any)?.errors?.[0]?.message || 'There was an error registering your account');
        return;
      }

      setIsLoading(false);
      [magicLinkErr, magicLinkRes] = await to(
        startMagicLinkFlowSignUp({
          redirectUrl: `https://${environmentContext.domain}/pages/verification`,
        }),
      );

      if (magicLinkErr || !magicLinkRes) {
        startOver(cleanClerkError(magicLinkErr, 'There was an error sending your magic link'));
        return;
      }

      verification = signupResponse.verifications.emailAddress;
    }

    if (verification.verifiedFromTheSameClient()) {
      setVerified(true);
      return;
    } else if (verification.status === 'expired') {
      setExpired(true);
    }

    if (magicLinkRes?.status === 'complete') {
      setSession(magicLinkRes.createdSessionId, () => navigate(`https://${environmentContext.domain}/pages/profile`));
      return;
    }
  };

  useEffect(() => {
    prepareMagicLink();
  }, []);

  if (isLoading) {
    return (
      <StatusContainer title="Loading">
        <Spinner visible={true} />
      </StatusContainer>
    );
  }

  if (expired) {
    return <StatusContainer title="Sorry">Your magic link has expired</StatusContainer>;
  }

  if (verified) {
    return <StatusContainer title="Success">Signed in on other tab</StatusContainer>;
  }

  return <StatusContainer title="Thank you">Please check your email for the magic sign-in link</StatusContainer>;
};

export default MagicLinkValidation;
